import React, { useEffect, useState } from "react";
import {
  Box,
  VStack,
  HStack,
  SimpleGrid,
  Text,
  Button,
  Input,
  Select,
  Switch,
  FormControl,
  FormLabel,
  useColorModeValue,
  useToast,
  Spinner,
  Divider,
} from "@chakra-ui/react";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import PageShell from "../layout/PageShell";

const TAX_DOC = doc(db, "systemConfig", "taxes");

const EMPTY_EXEMPTION = { enabled: false, from: "", to: "", rate: 0 };

const DEFAULT_TAXES = [
  { id: "tax1", taxName: "TVQ", taxValue: 9.975, fiscalType: "P", exemption: { ...EMPTY_EXEMPTION } },
  { id: "tax2", taxName: "TPS", taxValue: 5, fiscalType: "F", exemption: { ...EMPTY_EXEMPTION } },
  { id: "tax3", taxName: "Quebec-alcohol", taxValue: 0, fiscalType: "S", exemption: { ...EMPTY_EXEMPTION } },
];

const asRule = (value: any) => ({
  enabled: value?.enabled === true,
  from: String(value?.from || ""),
  to: String(value?.to || ""),
  rate: Math.max(0, Number(value?.rate) || 0),
});

const normalize = (taxes: any[]) => DEFAULT_TAXES.map((fallback, index) => {
  const tax = taxes?.[index] || {};
  return {
    id: fallback.id,
    taxName: String(tax.taxName ?? fallback.taxName),
    taxValue: Number(tax.taxValue ?? fallback.taxValue) || 0,
    fiscalType: ["F", "P", "S"].includes(tax.fiscalType) ? tax.fiscalType : fallback.fiscalType,
    exemption: asRule(tax.exemption),
  };
});

const ruleError = (rule: { enabled: boolean; from: string; to: string }) => {
  if (!rule.enabled) return "";
  if (!rule.from) return "Enter the start date and time.";
  if (rule.to && rule.to < rule.from) return "The end must be after the start.";
  return "";
};

const TaxSettings: React.FC = () => {
  const toast = useToast();
  const { currentUser } = useAuth();
  const [taxes, setTaxes] = useState(DEFAULT_TAXES);
  const [saved, setSaved] = useState(DEFAULT_TAXES);
  const [editing, setEditing] = useState(false);
  const [allowAllStoreTaxOverride, setAllowAllStoreTaxOverride] = useState(false);
  const [allowAllStoreOverride, setAllowAllStoreOverride] = useState(false);
  const [savedTaxOverride, setSavedTaxOverride] = useState(false);
  const [savedExemptionOverride, setSavedExemptionOverride] = useState(false);
  const [overrideEditing, setOverrideEditing] = useState(false);
  const [overrideWord, setOverrideWord] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textSecondary = useColorModeValue("gray.500", "gray.400");

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(TAX_DOC);
        if (snap.exists()) {
          const data = snap.data();
          const taxOn = data?.allowAllStoreTaxOverride === true;
          const exemptionOn = data?.allowAllStoreOverride === true;
          setAllowAllStoreTaxOverride(taxOn);
          setAllowAllStoreOverride(exemptionOn);
          setSavedTaxOverride(taxOn);
          setSavedExemptionOverride(exemptionOn);
          if (Array.isArray(data?.taxes)) {
            const loaded = normalize(data.taxes);
            setTaxes(loaded);
            setSaved(loaded);
          }
        }
      } catch (error) {
        console.error("Error loading taxes:", error);
        toast({ title: "Could not load taxes", status: "error", duration: 4000, isClosable: true });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [toast]);

  const updateTax = (index: number, patch: Record<string, unknown>) => {
    setTaxes((prev) => prev.map((tax, itemIndex) => (itemIndex === index ? { ...tax, ...patch } : tax)));
  };

  const updateRule = (index: number, patch: Record<string, unknown>) => {
    setTaxes((prev) => prev.map((tax, itemIndex) => (
      itemIndex === index ? { ...tax, exemption: { ...tax.exemption, ...patch } } : tax
    )));
  };

  const save = async () => {
    const invalid = taxes.map((tax) => ruleError(tax.exemption)).find(Boolean);
    if (invalid) {
      toast({ title: invalid, status: "error", duration: 4000, isClosable: true });
      return;
    }
    setSaving(true);
    try {
      await setDoc(TAX_DOC, {
        taxes: taxes.map((tax) => ({
          ...tax,
          taxName: tax.taxName.trim(),
          taxValue: Number(tax.taxValue) || 0,
          exemption: asRule(tax.exemption),
        })),
        allowAllStoreTaxOverride: savedTaxOverride,
        allowAllStoreOverride: savedExemptionOverride,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.email || "unknown",
      });
      const stored = taxes.map((tax) => ({
        ...tax,
        taxName: tax.taxName.trim(),
        taxValue: Number(tax.taxValue) || 0,
        exemption: asRule(tax.exemption),
      }));
      setTaxes(stored);
      setSaved(stored);
      setEditing(false);
      toast({ title: "Taxes saved", status: "success", duration: 2500, isClosable: true });
    } catch (error: any) {
      toast({ title: "Taxes could not be saved", description: error?.message, status: "error", duration: 5000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  const overrideChanged = allowAllStoreTaxOverride !== savedTaxOverride || allowAllStoreOverride !== savedExemptionOverride;
  const overrideConfirmed = overrideWord.trim().toLowerCase() === "override";

  const cancelOverride = () => {
    setAllowAllStoreTaxOverride(savedTaxOverride);
    setAllowAllStoreOverride(savedExemptionOverride);
    setOverrideWord("");
    setOverrideEditing(false);
  };

  const saveOverride = async () => {
    if (!overrideChanged || !overrideConfirmed) return;
    setSaving(true);
    try {
      await setDoc(TAX_DOC, {
        allowAllStoreTaxOverride,
        allowAllStoreOverride,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.email || "unknown",
      }, { merge: true });
      setSavedTaxOverride(allowAllStoreTaxOverride);
      setSavedExemptionOverride(allowAllStoreOverride);
      setOverrideWord("");
      setOverrideEditing(false);
      toast({ title: "Override saved", status: "success", duration: 2500, isClosable: true });
    } catch (error: any) {
      toast({ title: "The override could not be saved", description: error?.message, status: "error", duration: 4000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageShell
      title="Taxes"
      description="These taxes and exemptions apply to every store. A store can choose its items and can replace an exemption. It cannot change the name, rate, or fiscal type."
    >
      {loading ? <Spinner /> : (
        <VStack spacing={4} align="stretch">
          <Box bg={bgCard} border="1px" borderColor={borderColor} borderRadius="lg" p={5}>
            <Text fontWeight="600" mb={3}>Override</Text>
            <FormControl display="flex" alignItems="center" justifyContent="space-between" mb={4}>
              <Box mr={4}>
                <FormLabel mb={0}>Allow all stores tax override</FormLabel>
                <Text fontSize="sm" color={textSecondary}>Off by default. One switch for every store. When this is on, a store can change the tax name, rate, and fiscal type.</Text>
              </Box>
              <Switch isDisabled={!overrideEditing} isChecked={allowAllStoreTaxOverride} onChange={(event) => setAllowAllStoreTaxOverride(event.target.checked)} />
            </FormControl>
            <FormControl display="flex" alignItems="center" justifyContent="space-between">
              <Box mr={4}>
                <FormLabel mb={0}>Allow all stores exemption override</FormLabel>
                <Text fontSize="sm" color={textSecondary}>Off by default. One switch for every store. When this is on, a store can save its own exemption.</Text>
              </Box>
              <Switch isDisabled={!overrideEditing} isChecked={allowAllStoreOverride} onChange={(event) => setAllowAllStoreOverride(event.target.checked)} />
            </FormControl>
            {overrideEditing && (
              <Box mt={4} border="1px" borderColor="red.300" bg="red.50" borderRadius="md" p={4}>
                <Text fontWeight="700" color="red.700" mb={2}>Warning</Text>
                <Text fontSize="sm" color="red.800" mb={3}>
                  Turning either switch on lets every store depart from the tax set here. An incorrect tax or exemption can breach Revenu Québec rules and can lead to audits, penalties, and fines. Type override to confirm this change.
                </Text>
                <FormControl mb={3}>
                  <FormLabel fontSize="sm">Type override</FormLabel>
                  <Input value={overrideWord} autoComplete="off" onChange={(event) => setOverrideWord(event.target.value)} />
                </FormControl>
                <HStack justify="flex-end">
                  <Button variant="outline" isDisabled={saving} onClick={cancelOverride}>Cancel</Button>
                  <Button onClick={saveOverride} isLoading={saving} isDisabled={!overrideChanged || !overrideConfirmed}>Save</Button>
                </HStack>
              </Box>
            )}
            {!overrideEditing && (
              <HStack justify="flex-end" mt={4}>
                <Button onClick={() => { setOverrideWord(""); setOverrideEditing(true); }}>Edit</Button>
              </HStack>
            )}
          </Box>
          {taxes.map((tax, index) => (
            <Box key={tax.id} bg={bgCard} border="1px" borderColor={borderColor} borderRadius="lg" p={5}>
              <Text fontWeight="600" mb={3}>Tax {index + 1}</Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                <FormControl>
                  <FormLabel fontSize="sm">Tax name</FormLabel>
                  <Input isDisabled={!editing} value={tax.taxName} onChange={(event) => updateTax(index, { taxName: event.target.value })} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Rate (%)</FormLabel>
                  <Input isDisabled={!editing} type="number" min={0} step="0.0001" value={tax.taxValue} onChange={(event) => updateTax(index, { taxValue: Number(event.target.value) })} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Fiscal type</FormLabel>
                  <Select isDisabled={!editing} value={tax.fiscalType} onChange={(event) => updateTax(index, { fiscalType: event.target.value })}>
                    <option value="F">Federal (F)</option>
                    <option value="P">Provincial (P)</option>
                    <option value="S">Additional (S)</option>
                  </Select>
                </FormControl>
              </SimpleGrid>
              <Divider my={4} />
              <FormControl display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <Box>
                  <FormLabel mb="0" fontWeight="600">Exemption active</FormLabel>
                  <Text fontSize="xs" color={textSecondary}>When this is on, every store uses this exemption unless that store has its own.</Text>
                </Box>
                <Switch isDisabled={!editing} isChecked={tax.exemption.enabled} onChange={(event) => updateRule(index, { enabled: event.target.checked })} />
              </FormControl>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                <FormControl>
                  <FormLabel fontSize="sm">Start date and time</FormLabel>
                  <Input isDisabled={!editing} type="datetime-local" value={tax.exemption.from} onChange={(event) => updateRule(index, { from: event.target.value })} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">End date and time</FormLabel>
                  <Input isDisabled={!editing} type="datetime-local" value={tax.exemption.to} onChange={(event) => updateRule(index, { to: event.target.value })} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Rate during exemption (%)</FormLabel>
                  <Input isDisabled={!editing} type="number" min={0} step="0.0001" value={tax.exemption.rate} onChange={(event) => updateRule(index, { rate: Number(event.target.value) })} />
                </FormControl>
              </SimpleGrid>
              <Text mt={2} fontSize="xs" color={ruleError(tax.exemption) ? "red.500" : textSecondary}>
                {ruleError(tax.exemption) || "Leave the end empty to keep the exemption until it is turned off. A rate of 0 removes the tax for that period."}
              </Text>
            </Box>
          ))}
          <HStack justify="flex-end" spacing={3}>
            {editing ? (
              <>
                <Button variant="outline" isDisabled={saving} onClick={() => { setTaxes(saved); setEditing(false); }}>
                  Cancel
                </Button>
                <Button onClick={save} isLoading={saving}>
                  Save
                </Button>
              </>
            ) : (
              <Button onClick={() => setEditing(true)}>
                Edit
              </Button>
            )}
          </HStack>
        </VStack>
      )}
    </PageShell>
  );
};

export default TaxSettings;
